import { useState } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import {
  useProductSelector,
  ProductGroupsActions,
} from '@wajeez-web/stores/product';
import { Button } from '@wajeez-web/components/ui';
import { PlusIcon } from '@heroicons/react/solid';
import { DataTableWrapper, Header } from '@wajeez-web/components/helpers';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { Server } from '@wajeez/wajeez.microservices';

import { ProductGroupModal } from '../components/ProductGroupModal';
import { SetProductsToProductGroupModal } from '../components/SetProductsToProductGroupModal';

const ProductGroupsPage = () => {
  const [isProductGroupModalOpen, setIsProductGroupModalOpen] = useState(false);
  const [
    isSetProductsToProductGroupModalOpen,
    setIsSetProductsToProductGroupModalOpen,
  ] = useState(false);
  const [selectedProductGroup, setSelectedProductGroup] =
    useState<Partial<Server.ProductGroupOutput>>();

  const datatable = useProductSelector(
    ({ productGroups }) => productGroups.productGroupsDatatable
  );

  const { tablePreferances, setTablePreferences, isTableReferencesLoading } =
    HandleTableData(
      'productGroups',
      ProductGroupsActions.getProductGroupsDatatableAsync
    );

  return (
    <div>
      <PageLayoutsContainer
        title="Product Groups"
        endSideComponent={[
          {
            element: (
              <Button
                title={'Create Product Group'}
                bg={'primary'}
                color={'white'}
                Icon={PlusIcon}
                onClick={() => {
                  setIsProductGroupModalOpen(true);
                }}
              />
            ),
            permission: [AdminSystemPermissions.productGroups.add],
          },
        ]}
        withoutRightPadding={true}
        breadCrumbsLinks={[{ title: 'Product Groups' }]}
      >
        <DataTableWrapper
          data={datatable.data as contentEntityDataType}
          isLoading={datatable.status === 'loading' || isTableReferencesLoading}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: Header) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          rowActions={(row) => [
            {
              title: 'Edit',
              permission: AdminSystemPermissions.productGroups.edit,
              type: 'request',
              endpoint: '',
              action: () => {
                setSelectedProductGroup({
                  id: Number(row[1]),
                });
                setIsProductGroupModalOpen(true);
              },
            },
            {
              title: 'Set Products',
              permission: AdminSystemPermissions.productGroups.setProducts,
              type: 'request',
              endpoint: '',
              action: () => {
                setSelectedProductGroup({
                  id: Number(row[1]),
                });
                setIsSetProductsToProductGroupModalOpen(true);
              },
            },
          ]}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
        />
      </PageLayoutsContainer>
      {isProductGroupModalOpen && (
        <ProductGroupModal
          isOpen={isProductGroupModalOpen}
          onClose={() => {
            setIsProductGroupModalOpen(false);
            setSelectedProductGroup(undefined);
          }}
          onSubmit={() => {
            setIsProductGroupModalOpen(false);

            if (selectedProductGroup?.id) {
              setSelectedProductGroup(undefined);
            }

            tablePreferances && setTablePreferences({ ...tablePreferances });
          }}
          id={selectedProductGroup?.id}
        />
      )}
      {isSetProductsToProductGroupModalOpen && selectedProductGroup?.id && (
        <SetProductsToProductGroupModal
          isOpen={isSetProductsToProductGroupModalOpen}
          onClose={() => {
            setIsSetProductsToProductGroupModalOpen(false);
            setSelectedProductGroup(undefined);
          }}
          onSubmit={() => {
            setIsSetProductsToProductGroupModalOpen(false);

            if (selectedProductGroup?.id) {
              setSelectedProductGroup(undefined);
            }

            tablePreferances && setTablePreferences({ ...tablePreferances });
          }}
          id={selectedProductGroup.id}
        />
      )}
    </div>
  );
};

export default ProductGroupsPage;
